import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { LoginPromptModal } from '@/components/common/LoginPromptModal';
import { restaurantService } from '@/services/restaurant';
import { reviewService } from '@/services/review';
import { useReviewStats } from '@/hooks/useReviewStats';
import { supabase } from '@/lib/supabase';

interface Review {
  id: string;
  order_id?: string;
  rating: number;
  comment: string;
  image_urls?: string[];
  is_recommended: boolean;
  reviewer_nickname?: string;
  created_at: string;
  updated_at: string;
}

interface Restaurant {
  id: string;
  name: string;
  rating: number;
  total_reviews: number;
}

interface StoreReviewsPageProps {
  storeId?: string;
  readonly?: boolean;
}

export function StoreReviewsPage({ storeId: propStoreId, readonly = false }: StoreReviewsPageProps = {}) {
  const { storeId: paramStoreId } = useParams<{ storeId: string }>();
  const actualStoreId = propStoreId || paramStoreId;
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const [searchParams] = useSearchParams();
  const highlightOrderId = searchParams.get('highlightOrderId');
  
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'recommended' | 'photo'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'rating_high' | 'rating_low'>('newest');
  const [reportedReviews, setReportedReviews] = useState<Set<string>>(new Set());
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [expandedImageUrl, setExpandedImageUrl] = useState<string | null>(null);
  
  // 리뷰 통계 계산
  const { calculatedRating, calculatedTotalReviews, isLoadingReviewStats } = useReviewStats(actualStoreId || null);
  const [highlightedReviewId, setHighlightedReviewId] = useState<string | null>(null);

  // 리뷰 데이터 로드
  useEffect(() => {
    const fetchReviews = async () => {
      if (!actualStoreId) return;
      
      try {
        setIsLoading(true);
        
        // 매장 정보 조회
        const restaurantResult = await restaurantService.getRestaurantById(actualStoreId);
        if (restaurantResult.success && restaurantResult.data) {
          // Convert to local Restaurant interface
          const localRestaurant: Restaurant = {
            id: restaurantResult.data.id,
            name: restaurantResult.data.name,
            rating: restaurantResult.data.rating || 0,
            total_reviews: restaurantResult.data.total_reviews || 0
          };
          setRestaurant(localRestaurant);
        }
        
        // 리뷰 목록 조회 (reviewService 사용)
        const reviewsResult = await reviewService.getReviewsByRestaurant(actualStoreId);
        if (reviewsResult && reviewsResult.length > 0) {
          // DB의 Review를 로컬 Review 타입에 맞게 변환
          const convertedReviews: Review[] = reviewsResult.map(review => ({
            id: review.id,
            order_id: review.order_id,
            rating: review.rating,
            comment: review.comment || '',
            // review.images 배열에서 image_url만 추출하여 sequence 순서로 정렬
            image_urls: review.images ? 
              review.images
                .sort((a, b) => a.sequence - b.sequence)
                .map(img => img.image_url) 
              : [],
            is_recommended: review.rating >= 4, // 별점 4점 이상을 추천으로 간주
            reviewer_nickname: review.reviewer_nickname,
            created_at: review.created_at,
            updated_at: review.updated_at
          }));
          setReviews(convertedReviews);
        }
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
        showToast('리뷰를 불러오는데 실패했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviews();
  }, [actualStoreId]);

  // 하이라이트할 리뷰 찾기 및 스크롤
  useEffect(() => {
    if (highlightOrderId && reviews.length > 0) {
      // order_id로 리뷰 찾기
      const targetReview = reviews.find(r => r.order_id === highlightOrderId);
      if (targetReview) {
        setHighlightedReviewId(targetReview.id);
        // 해당 리뷰로 스크롤
        setTimeout(() => {
          const element = document.getElementById(`review-${targetReview.id}`);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    }
  }, [highlightOrderId, reviews]);

  // 확대 보기 ESC/스크롤 잠금 처리
  useEffect(() => {
    if (!expandedImageUrl) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpandedImageUrl(null);
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [expandedImageUrl]);

  // 필터링 및 정렬된 리뷰 목록
  const filteredAndSortedReviews = reviews
    .filter(review => {
      switch (filterType) {
        case 'recommended':
          return review.is_recommended;
        case 'photo':
          return review.image_urls && review.image_urls.length > 0;
        default:
          return true;
      }
    })
    .sort((a, b) => {
      switch (sortOrder) {
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'rating_high':
          return b.rating - a.rating;
        case 'rating_low':
          return a.rating - b.rating;
        default: // newest
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

  // 신고 버튼 클릭 시
  const handleReportClick = async (reviewId: string) => {
    if (readonly) return; // 읽기 전용 모드에서는 신고 비활성화
    
    if (reportedReviews.has(reviewId)) {
      showToast('이미 신고한 리뷰입니다');
      return;
    }

    // 로그인 상태 확인
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoginPromptOpen(true);
      return;
    }

    setSelectedReviewId(reviewId);
    setReportModalOpen(true);
  };

  // 신고 확인
  const handleConfirmReport = async () => {
    if (!selectedReviewId) return;

    try {
      // 현재 로그인한 사용자 ID 가져오기
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        showToast('로그인이 필요합니다');
        setReportModalOpen(false);
        setLoginPromptOpen(true);
        return;
      }
      
      const result = await reviewService.reportReview(selectedReviewId, user.id, '부적절한 내용');
      
      if (result.success) {
        setReportedReviews(prev => new Set(prev).add(selectedReviewId));
        setReportModalOpen(false);
        showToast('신고되었습니다');
      } else {
        showToast('신고 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('Failed to report review:', error);
      showToast('신고 처리에 실패했습니다');
    }
    setSelectedReviewId(null);
  };

  // 신고 취소
  const handleCancelReport = () => {
    setReportModalOpen(false);
    setSelectedReviewId(null);
    showToast('신고가 취소되었습니다');
  };

  // 날짜 포맷팅
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // 별점 렌더링
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">리뷰를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* 헤더 */}
      {!readonly && (
        <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold">리뷰</h1>
            </div>
          </div>
        </div>
      )}

      {/* 매장 정보 및 통계 */}
      {restaurant && (
        <div className="px-4 py-6 border-b border-gray-200">
          <h2 className="text-xl font-bold mb-2">{restaurant.name}</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {renderStars(Math.floor(calculatedRating))}
              </div>
              <span className="text-lg font-semibold">{calculatedRating.toFixed(1)}</span>
              {isLoadingReviewStats && <span className="text-xs text-gray-500 ml-1">...</span>}
            </div>
            <span className="text-gray-600">리뷰 {calculatedTotalReviews}개</span>
          </div>
        </div>
      )}

      {/* 필터 및 정렬 */}
      <div className="px-4 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-1 rounded-full text-sm ${
                filterType === 'all'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              전체
            </button>
            <button
              onClick={() => setFilterType('recommended')}
              className={`px-3 py-1 rounded-full text-sm ${
                filterType === 'recommended'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              추천
            </button>
            <button
              onClick={() => setFilterType('photo')}
              className={`px-3 py-1 rounded-full text-sm ${
                filterType === 'photo'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              사진
            </button>
          </div>
          
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as typeof sortOrder)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1"
          >
            <option value="newest">최신순</option>
            <option value="oldest">오래된순</option>
            <option value="rating_high">별점 높은순</option>
            <option value="rating_low">별점 낮은순</option>
          </select>
        </div>
      </div>

      {/* 리뷰 목록 */}
      <div className="px-4 py-4">
        {filteredAndSortedReviews.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {filterType === 'all' ? '아직 리뷰가 없습니다' : '조건에 맞는 리뷰가 없습니다'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAndSortedReviews.map((review) => (
              <div 
                key={review.id} 
                id={`review-${review.id}`}
                className={`border border-gray-200 rounded-lg p-4 bg-white ${
                  highlightedReviewId === review.id 
                    ? 'border-2 border-green-400 bg-green-50 shadow-md' 
                    : 'hover:shadow-sm transition-shadow'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">
                          {review.reviewer_nickname || '익명'}
                        </span>
                        {review.is_recommended && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            추천
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center">
                          {renderStars(review.rating)}
                        </div>
                        <span className="text-sm text-gray-500">{formatDate(review.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {!readonly && (
                    <button
                      onClick={() => handleReportClick(review.id)}
                      className={`text-sm px-2 py-1 rounded ${
                        reportedReviews.has(review.id)
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      disabled={reportedReviews.has(review.id)}
                    >
                      신고
                    </button>
                  )}
                </div>
                
                {review.comment && (
                  <p className="text-gray-800 mb-3 leading-relaxed">{review.comment}</p>
                )}
                
                {review.image_urls && review.image_urls.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto mt-3">
                    {review.image_urls.map((imageUrl, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() =>
                          setExpandedImageUrl(prev =>
                            prev === imageUrl ? null : imageUrl
                          )
                        }
                        className="relative flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-lg"
                        aria-label="리뷰 사진 확대"
                      >
                        <img
                          src={imageUrl}
                          alt={`리뷰 이미지 ${index + 1}`}
                          className="w-24 h-24 object-cover rounded-lg border border-gray-200 cursor-zoom-in"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <ToastContainer />

      {expandedImageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4"
          onClick={() => setExpandedImageUrl(null)}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={expandedImageUrl}
            alt="확대된 리뷰 이미지"
            className="max-h-[90vh] max-w-full rounded-xl shadow-2xl cursor-zoom-out"
            onError={() => setExpandedImageUrl(null)}
          />
        </div>
      )}
      
      {!readonly && (
        <>
          <ConfirmModal
            isOpen={reportModalOpen}
            title="정말 신고하시겠습니까?"
            onConfirm={handleConfirmReport}
            onCancel={handleCancelReport}
          />
          
          <LoginPromptModal
            isOpen={loginPromptOpen}
            onClose={() => setLoginPromptOpen(false)}
          />
        </>
      )}
    </div>
  );
}
